import { type SchedulePreferencesPayload } from '@/features/accounts/api';
import { parseLocalDate, toIsoDate } from '@/shared/i18n/dateUtils';
import type { CatalogFacility } from '@catalog';
import { Schedule } from '@/features/accounts/types';
import { selectionIncludesAsc } from './ascRules';
import { acceptsLaterDate, ConfigurationFormData } from './formData';

const DEFAULT_NOTICE_DAYS = '3';
const DEFAULT_WINDOW_MONTHS = 1;

export function defaultFormValues(
  schedule: Schedule,
  preferences?: SchedulePreferencesPayload
): ConfigurationFormData {
  const interest = schedule.date_interest;
  const accept = preferences?.accept ?? interest.accept;
  const exclude = preferences?.exclude ?? interest.exclude;

  return {
    windows:
      accept.length > 0
        ? accept.map((range) => ({
          from: range.from,
          to: range.to,
          weekdays: range.appointment_days,
        }))
        : [suggestedWindow()],
    blackouts: exclude.map((range) => ({ from: range.from, to: range.to })),
    maxGapAscConsular: numberField(
      preferences?.max_gap_between_asc_and_consular ?? interest.max_gap_between_asc_and_consular
    ),
    minDaysFromNow: interest.configured
      ? numberField(preferences?.min_days_from_now ?? interest.min_days_from_now)
      : numberField(preferences?.min_days_from_now) || DEFAULT_NOTICE_DAYS,
    minSameDayGap: numberField(preferences?.min_same_day_gap ?? interest.min_same_day_gap),
    allowCancellation: preferences?.allow_cancellation ?? interest.allow_cancellation,
    acceptWorseDate: preferences?.accept_worse_date ?? interest.accept_worse_date,
    preferredFacilities: preferences?.preferred_facilities ?? facilityCodes(schedule),
  };
}

export function toPreferencesPayload(
  data: ConfigurationFormData,
  facilities: CatalogFacility[]
): SchedulePreferencesPayload {
  const ascRulesApply = selectionIncludesAsc(facilities, data.preferredFacilities);

  return {
    accept: data.windows.map((window) => ({
      from: window.from,
      to: window.to,
      appointment_days: window.weekdays,
    })),
    exclude: data.blackouts.map((blackout) => ({ from: blackout.from, to: blackout.to })),
    max_gap_between_asc_and_consular: ascRulesApply ? days(data.maxGapAscConsular) : null,
    min_days_from_now: days(data.minDaysFromNow),
    min_same_day_gap: ascRulesApply ? days(data.minSameDayGap) : null,
    allow_cancellation: data.allowCancellation,
    accept_worse_date: acceptsLaterDate(data),
    preferred_facilities: data.preferredFacilities,
  };
}

function days(field: string): number | null {
  return field ? Number.parseInt(field, 10) : null;
}

function suggestedWindow(): ConfigurationFormData['windows'][number] {
  const today = new Date();
  const until = parseLocalDate(toIsoDate(today));
  until.setMonth(until.getMonth() + DEFAULT_WINDOW_MONTHS);

  return { from: toIsoDate(today), to: toIsoDate(until), weekdays: [] };
}

function numberField(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : value.toString();
}

function facilityCodes(schedule: Schedule): string[] {
  if (!Array.isArray(schedule.preferred_facilities)) return [];

  return schedule.preferred_facilities.map((facility) => facility.code);
}
